import { useMemo } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import { Calendar, CheckSquare, Zap, Sparkles, Bot } from "lucide-react";
import clsx from "clsx";
import type { Card } from "../../types";
import { priorityMeta, categoryBg, categoryBorder, fmtDate, isOverdue, moodMeta } from "../../lib/utils";
import { useStore } from "../../stores/store";

interface Props {
  card: Card;
  onClick: () => void;
  dimmed?: boolean;
  overlay?: boolean;
}

export function CardView({ card, onClick, dimmed, overlay }: Props) {
  const sortableData = useMemo(
    () => ({ type: "card" as const, laneId: card.laneId }),
    [card.laneId],
  );
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
    data: sortableData,
    disabled: overlay,
    animateLayoutChanges: () => false,
  });

  const style = overlay
    ? undefined
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };

  const categories = useStore((s) => s.categories);
  const lanes = useStore((s) => s.lanes);
  const freshCardId = useStore((s) => s.freshCardId);
  const isFresh = freshCardId === card.id;
  const inDoneLane = lanes.find((l) => l.id === card.laneId)?.isDone ?? false;
  const catById = new Map(categories.map((c) => [c.id, c]));
  const cardCats = (card.categoryIds || []).map((id) => catById.get(id)).filter(Boolean) as typeof categories;

  const pm = priorityMeta[card.priority] ?? priorityMeta.medium;
  const mm = moodMeta[card.mood] ?? moodMeta.routine;
  const subtasks = Array.isArray(card.subtasks) ? card.subtasks : [];
  const doneSub = subtasks.filter((s) => s && s.done).length;
  const totalSub = subtasks.length;
  const progress = totalSub > 0 ? Math.round((doneSub / totalSub) * 100) : 0;
  const complete = progress === 100;
  const overdue = !card.completedAt && !inDoneLane && isOverdue(card.dueDate);
  const fromExternal = card.createdVia === "api";
  const fromAI = !fromExternal && (!!card.transcript || card.aiGenerated);
  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        borderLeftColor: mm.border,
        boxShadow: `inset 3px 0 0 -1px ${mm.border}44`,
        opacity: dimmed ? 0.35 : 1,
      }}
      {...(overlay ? {} : attributes)}
      {...(overlay ? {} : listeners)}
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onClick();
      }}
      className={clsx(
        "card-grad card-sheen group relative shrink-0 select-none touch-none rounded-xl border border-l-[3px] border-white/[0.07] p-3 shadow-card transition-opacity duration-150",
        !overlay && "cursor-grab active:cursor-grabbing hover:border-violet-400/40 hover:shadow-[0_14px_36px_-12px_rgba(168,85,247,0.45)]",
        isDragging && !overlay && "opacity-40",
        overlay && "rotate-[1.5deg] scale-[1.04] border-violet-400/60 shadow-[0_20px_56px_rgba(168,85,247,0.55)]",
      )}
    >
      {/* Halo "IAGO criou esse card" — gradiente da marca que esmaece */}
      {isFresh && !overlay && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-xl"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
          style={{
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.55), rgba(217,70,239,0.45), rgba(14,165,233,0.4))",
            filter: "blur(10px)",
          }}
        />
      )}

      {/* Gradient tint interno MUITO sutil — mood colorido no topo */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-16 rounded-t-xl opacity-50 transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: `linear-gradient(180deg, ${mm.border}12 0%, transparent 100%)` }}
      />
      {/* Top accent line */}
      <div className="pointer-events-none absolute inset-x-3 top-0 h-[2px] rounded-t bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className="relative flex items-start gap-2 pr-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="break-words text-[13.5px] font-semibold leading-snug text-slate-100 transition-colors group-hover:text-white">
              {card.title}
            </h3>
            {fromExternal ? (
              <motion.span
                initial={{ rotate: 0 }}
                whileHover={{ rotate: -8, scale: 1.2 }}
                transition={{ type: "spring", damping: 12 }}
                className="mt-0.5 shrink-0"
                title="Criado por integração externa"
              >
                <Bot className="h-3.5 w-3.5 text-cyan-300 drop-shadow-[0_0_6px_rgba(34,211,238,0.6)]" />
              </motion.span>
            ) : fromAI && (
              <motion.span
                initial={{ rotate: 0 }}
                whileHover={{ rotate: 20, scale: 1.2 }}
                transition={{ type: "spring", damping: 12 }}
                className="mt-0.5 shrink-0"
              >
                <Sparkles className="h-3 w-3 text-violet-400 drop-shadow-[0_0_6px_rgba(168,85,247,0.6)]" />
              </motion.span>
            )}
          </div>

          {card.whyItMatters && (
            <p className="mt-1 line-clamp-2 text-[11px] italic leading-relaxed text-slate-400">
              {card.whyItMatters}
            </p>
          )}

          {cardCats.length > 0 && (
            <motion.div
              className="mt-2 flex flex-wrap gap-1"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.04, delayChildren: 0.05 } },
              }}
            >
              {cardCats.slice(0, 4).map((cat) => (
                <motion.span
                  key={cat.id}
                  variants={{
                    hidden: { opacity: 0, y: 4, scale: 0.9 },
                    visible: { opacity: 1, y: 0, scale: 1 },
                  }}
                  className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium text-slate-100 transition hover:brightness-125"
                  style={{ backgroundColor: categoryBg(cat.color), borderColor: categoryBorder(cat.color) }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: cat.color, boxShadow: `0 0 6px ${cat.color}` }}
                  />
                  {cat.name}
                </motion.span>
              ))}
              {cardCats.length > 4 && (
                <span className="text-[10px] text-slate-500">+{cardCats.length - 4}</span>
              )}
            </motion.div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
            <span
              className={clsx(
                "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 ring-1 ring-inset transition hover:brightness-125",
                pm.glow,
                pm.ring,
                pm.color,
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current shadow-[0_0_6px_currentColor]" />
              {pm.label}
            </span>

            <span
              className={clsx("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px]", mm.text)}
              style={{ backgroundColor: mm.bg }}
              title={`Mood: ${mm.label}`}
            >
              {mm.label}
            </span>

            {card.effort && (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] text-slate-400">
                <Zap className="h-3 w-3" />
                {card.effort}
              </span>
            )}

            {totalSub > 0 && (
              <span className="inline-flex items-center gap-1">
                <CheckSquare className="h-3 w-3" />
                {doneSub}/{totalSub}
              </span>
            )}

            {card.dueDate && (
              <span
                className={clsx(
                  "inline-flex items-center gap-1 transition",
                  overdue ? "text-rose-300 [text-shadow:0_0_8px_rgba(239,68,68,0.5)]" : "text-slate-400",
                )}
              >
                <Calendar className="h-3 w-3" />
                {fmtDate(card.dueDate)}
              </span>
            )}
          </div>

          {totalSub > 0 && (
            <div className="relative mt-2 h-1 overflow-hidden rounded-full bg-white/5">
              <motion.div
                className={clsx(
                  "h-full rounded-full",
                  complete
                    ? "bg-gradient-to-r from-emerald-500 to-teal-400 shadow-[0_0_10px_rgba(16,185,129,0.6)]"
                    : "bg-gradient-to-r from-violet-500 via-fuchsia-400 to-violet-500",
                )}
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ type: "spring", damping: 22, stiffness: 260 }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
